import { useState, useEffect, useRef } from 'react'
import { IRoleFilters, IFilterConfig } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator, EFilterFetchConvertType } from '../types/enum'
import { useAtomValue } from 'jotai'
import * as state from '../states'
import { ROLE_IS_SYSTEM_OPTIONS, ROLE_EXTRA_IS_BUYER_OPTIONS } from '../constants'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'

type IUseFiltersRolePage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	roleFiltersConfig: IFilterConfig<keyof IRoleFilters>[];
	defaultFiltersValue: IRoleFilters;
	initialFiltersValue: IRoleFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IRoleFilters | null)) => (IRoleFilters | null)) | IRoleFilters | null)) => void;
}

const ROLE_FILTERS_DEFAULT: IRoleFilters = {
	code: '',
	name: '',
	'extra.inGroup': '',
	'extra.isBuyer': null,
	isSystem: null,
	notes: ''
}


export const useRoleFiltersPage = (): IUseFiltersRolePage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)

	const profile = useAtomValue(state.profileAtom)

	const [searchParams, setSearchParams] = useSearchParams()

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IRoleFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const ROLE_EXTRA_IS_BUYER_OPTIONS_TRANS = ROLE_EXTRA_IS_BUYER_OPTIONS(t)
	const ROLE_IS_SYSTEM_OPTIONS_TRANS = ROLE_IS_SYSTEM_OPTIONS(t)

	const roleFiltersConfig: IFilterConfig<keyof IRoleFilters>[] = [
		{
			name: 'code',
			label: t(ALL_LANG_KEY.role_list_table.column_code),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'name',
			label: t(ALL_LANG_KEY.role_list_table.column_name),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'extra.isBuyer',
			label: t(ALL_LANG_KEY.role_list_table.column_is_buyer),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: ROLE_EXTRA_IS_BUYER_OPTIONS_TRANS,
			styles: { minWidth: '200px' }
		},
		{
			name: 'isSystem',
			label: t(ALL_LANG_KEY.role_list_table.column_is_system),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: ROLE_IS_SYSTEM_OPTIONS_TRANS,
			styles: { minWidth: '200px' }
		},
		{
			name: 'extra.inGroup',
			label: t(ALL_LANG_KEY.role_list_table.column_in_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		}
		// {
		// 	name: 'notes',
		// 	label: 'Notes',
		// 	type: EFilterType.input,
		// 	operator: EFilterOperator.isLike,
		// 	specOperator: EFilterSpecOperator.everywhere,
		// 	styles: { width: '200px' }
		// },
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		roleFiltersConfig,
		ROLE_FILTERS_DEFAULT
	)

	useEffect(() => {
		//console.log('hook.useRoleFiltersPage useEffect::START:: ', { userFilters, filter })
		if (!!userFilters) {
			setSend(true)

			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				userFilters,
				roleFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				return prev
			})

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			console.log('hook.useRoleFiltersPage:: useEffect::END:: ', { userFilters, filter, newFilter, newFilterStr })
		}
	}, [userFilters])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		roleFiltersConfig,
		defaultFiltersValue: ROLE_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}